import type { IconName } from "@-/dev.improve.haniverse.assets.icons";
import type {
  OnboardingAnnouncedDestination,
  OnboardingPageReaction,
} from "@-/dev.improve.haniverse.lib.onboarding.state";

/**
 * The intro's questions, one `@onboarding/intro` file each. Each keeps its answer in its own field
 * of the onboarding state (`heardAbout`, `reasons`, `dailyGoalMinutes`), so a question is added or
 * removed in code, not by a file alone.
 */
export const ONBOARDING_INTRO_QUESTION_KINDS = ["heard-about", "reasons", "daily-goal"] as const;
export type OnboardingIntroQuestionKind = (typeof ONBOARDING_INTRO_QUESTION_KINDS)[number];

/** What the summary's text may say about the learner's goal: `{characters}` and `{minutes}`. */
export const ONBOARDING_SUMMARY_PLACEHOLDERS = ["characters", "minutes"] as const;
export type OnboardingSummaryPlaceholder = (typeof ONBOARDING_SUMMARY_PLACEHOLDERS)[number];

/** A page of plain text: its title, its paragraphs, and the label of the button that ends it. */
export interface OnboardingTextPage {
  button: string;
  /** Drawn after the button's label: the place the button takes the learner. */
  icon?: IconName;
  /** Plain text; a hard line break is a `\n`. */
  paragraphs: readonly string[];
  title: string;
}

export interface OnboardingWelcomePage extends OnboardingTextPage {
  kind: "welcome";
}

/** An answer to a question. `id` is what the state and analytics keep, so it never changes. */
export interface OnboardingSurveyOption {
  id: string;
  label: string;
}

export interface OnboardingReasonOption extends OnboardingSurveyOption {
  /** Shown beside the label, as Duolingo shows an illustration. */
  emoji: string;
}

export interface OnboardingDailyGoalOption {
  label: string;
  minutes: number;
  /** How the goal reads: Casual, Regular, Serious, Intense. */
  pace: string;
}

interface OnboardingQuestionPageBase {
  button: string;
  icon?: IconName;
  /** The question. */
  title: string;
}

export type OnboardingIntroQuestionPage =
  | (OnboardingQuestionPageBase & {
      kind: "heard-about";
      options: readonly OnboardingSurveyOption[];
    })
  | (OnboardingQuestionPageBase & {
      kind: "reasons";
      options: readonly OnboardingReasonOption[];
    })
  | (OnboardingQuestionPageBase & {
      kind: "daily-goal";
      options: readonly OnboardingDailyGoalOption[];
    });

/** One of the summary's achievements. Its text may hold `ONBOARDING_SUMMARY_PLACEHOLDERS`. */
export interface OnboardingSummaryItem {
  emoji: string;
  subtitle: string;
  title: string;
}

/** What the learner can achieve. Its title and items may hold `ONBOARDING_SUMMARY_PLACEHOLDERS`. */
export interface OnboardingSummaryPage extends OnboardingQuestionPageBase {
  items: readonly OnboardingSummaryItem[];
  kind: "summary";
}

export type OnboardingIntroPage =
  | OnboardingIntroQuestionPage
  | OnboardingSummaryPage
  | OnboardingWelcomePage;

/** A page of an introduction in the sheet. `id` names its button for tests. */
export interface OnboardingSheetPage extends OnboardingTextPage {
  id: string;
}

/** A destination the onboarding has unlocked, with the button that goes there. */
export interface OnboardingAnnouncement extends OnboardingTextPage {
  icon: IconName;
}

/** Everything the `@onboarding` files say, as `_build-onboarding` writes it. */
export interface OnboardingContent {
  announcements: Readonly<Record<OnboardingAnnouncedDestination, OnboardingAnnouncement>>;
  /** The globe's pages, in order: the welcome, the questions, and the summary last. */
  intro: readonly [OnboardingWelcomePage, ...OnboardingIntroQuestionPage[], OnboardingSummaryPage];
  /** Each page's introduction, played the first time the learner opens it. */
  introductions: Readonly<
    Record<OnboardingPageReaction, readonly [OnboardingSheetPage, ...OnboardingSheetPage[]]>
  >;
}
